import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { txt } from '../lib/enums.js';

/**
 * Kanban reusable: una lista con maquina de estados, dibujada por columnas.
 *
 * Lo usan cinco pantallas —tickets, remesas, recepciones, padron de equipos y
 * guias— y por eso es una pieza y no cinco copias. Lo unico que cambia entre
 * ellas es de donde salen las columnas y que se dibuja en cada tarjeta.
 *
 * ---------------------------------------------------------------------------
 * LAS COLUMNAS SALEN DEL FLUJO QUE DE VERDAD ESTA
 *
 * No son fijas. Una lista de tickets de campo no tiene por que ensenar las
 * nueve columnas del staging, y una de staging no tiene por que ensenar las
 * de campo; pero una lista mezclada necesita las dos. `columnasPresentes`
 * resuelve eso mirando los datos, igual que el prototipo.
 *
 * Una columna del flujo elegido se dibuja AUNQUE ESTE VACIA: que no haya
 * nada en "En trabajo" es informacion, y esconder la columna la convierte en
 * una ausencia que nadie nota.
 */

export interface FlujoKanban {
  /** Los estados de este flujo, en el orden en que se recorren. */
  readonly estados: readonly string[];
  /** Si alguno de estos estados aparece, el flujo esta presente. */
  readonly reconoce?: (estado: string) => boolean;
}

/**
 * Las columnas a dibujar, dados los flujos posibles y lo que hay en la lista.
 *
 * Un flujo entra si alguno de sus estados aparece en los datos. Si ninguno
 * aparece —la lista esta vacia— entra el primero, que es el flujo principal
 * de esa pantalla: una rejilla sin columnas no dice nada, y con las columnas
 * del flujo esperado dice "aqui no hay nada todavia".
 */
export function columnasPresentes(
  estados: readonly string[],
  flujos: readonly FlujoKanban[],
): readonly string[] {
  const hay = new Set(estados);
  const presentes = flujos.filter(
    (f) => f.estados.some((e) => hay.has(e)) || (f.reconoce ? [...hay].some(f.reconoce) : false),
  );
  const elegidos = presentes.length > 0 ? presentes : flujos.slice(0, 1);

  /* Sin repetir: dos flujos pueden compartir un estado terminal. */
  const vistas = new Set<string>();
  const columnas: string[] = [];
  for (const f of elegidos) {
    for (const e of f.estados) {
      if (!vistas.has(e)) {
        vistas.add(e);
        columnas.push(e);
      }
    }
  }
  /* Un estado que esta en los datos y en ningun flujo se dibuja al final, en
     vez de desaparecer: una tarjeta que no se ve en ninguna columna es una
     tarjeta perdida, y nadie la busca porque nadie sabe que falta. */
  for (const e of estados) {
    if (!vistas.has(e)) {
      vistas.add(e);
      columnas.push(e);
    }
  }
  return columnas;
}

export interface PropsKanban<T> {
  items: readonly T[];
  /** El estado de un elemento. Decide su columna. */
  estadoDe: (item: T) => string;
  /** Llave estable para React. */
  claveDe: (item: T) => string;
  columnas: readonly string[];
  /** Nombres legibles de los estados. Nunca se pinta el valor crudo. */
  etiquetas: Record<string, string>;
  /** Clase del chip por estado, si la pantalla tiene una. */
  clases?: Record<string, string>;
  /** El contenido de la tarjeta. */
  tarjeta: (item: T) => ReactNode;
  /** Abrir el elemento. Si se da, la tarjeta es operable con teclado. */
  alAbrir?: (item: T) => void;
  /** Cuantas tarjetas se dibujan por columna antes de resumir el resto. */
  tope?: number;
}

export function Kanban<T>({
  items,
  estadoDe,
  claveDe,
  columnas,
  etiquetas,
  clases,
  tarjeta,
  alAbrir,
  tope = 50,
}: PropsKanban<T>): JSX.Element {
  const porColumna = new Map<string, T[]>();
  for (const c of columnas) porColumna.set(c, []);
  for (const it of items) {
    const e = estadoDe(it);
    const lista = porColumna.get(e);
    if (lista) lista.push(it);
  }

  return (
    <div className="kanban" data-prueba="kanban">
      {columnas.map((c) => {
        const lista = porColumna.get(c) ?? [];
        const visibles = lista.slice(0, tope);
        return (
          <section key={c} className="kan-col" aria-label={txt(etiquetas, c)}>
            <header className="kan-cab">
              <span className={`chip ${clases?.[c] ?? ''}`}>{txt(etiquetas, c)}</span>
              {/* La cuenta es del total, no de lo dibujado: si dice 12 y se
                  ven 50, la cuenta mentiria sobre el tamano del trabajo. */}
              <b data-prueba="kan-cuenta">{lista.length}</b>
            </header>

            <div className="kan-cuerpo">
              {visibles.map((it) => {
                const contenido = <div className="kan-tarjeta">{tarjeta(it)}</div>;
                if (!alAbrir) return <div key={claveDe(it)}>{contenido}</div>;
                return (
                  /* Un boton y no un div con onClick: asi llega el teclado,
                     el lector de pantalla lo anuncia como accionable, y no
                     hay que reimplementar Enter y Espacio a mano. */
                  <button
                    key={claveDe(it)}
                    type="button"
                    className="kan-abrir"
                    onClick={() => alAbrir(it)}
                    data-prueba="kan-tarjeta"
                  >
                    {contenido}
                  </button>
                );
              })}

              {lista.length > visibles.length && (
                <p className="mini kan-mas">
                  y {lista.length - visibles.length} mas. Acota con los filtros para verlas.
                </p>
              )}

              {lista.length === 0 && <p className="mini kan-vacia">Sin elementos</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * El conmutador Kanban / Tabla, con memoria por pantalla.
 *
 * La eleccion se guarda en el navegador y no en el servidor: es una
 * preferencia de quien mira, no un dato de la operacion. Y se guarda POR
 * PANTALLA porque no es la misma decision en tickets que en guias.
 */
export function useVista(
  pantalla: string,
): [vista: 'kanban' | 'tabla', poner: (v: 'kanban' | 'tabla') => void] {
  const llave = `vista:${pantalla}`;
  const [vista, setVista] = useState<'kanban' | 'tabla'>(() => {
    try {
      return localStorage.getItem(llave) === 'tabla' ? 'tabla' : 'kanban';
    } catch {
      /* Ventana privada, almacenamiento bloqueado: se sigue con el valor por
         omision en vez de reventar la pantalla por una preferencia. */
      return 'kanban';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(llave, vista);
    } catch {
      /* Igual que arriba: no poder recordar la preferencia no es una falla
         que deba ver la persona. */
    }
  }, [llave, vista]);

  return [vista, setVista];
}

export function ConmutadorVista({
  vista,
  poner,
}: {
  vista: 'kanban' | 'tabla';
  poner: (v: 'kanban' | 'tabla') => void;
}): JSX.Element {
  return (
    <div className="conmutador" role="group" aria-label="Forma de ver la lista">
      <button
        type="button"
        className={`chip btn ${vista === 'kanban' ? 'sel' : ''}`}
        aria-pressed={vista === 'kanban'}
        onClick={() => poner('kanban')}
        data-prueba="ver-kanban"
      >
        Kanban
      </button>
      <button
        type="button"
        className={`chip btn ${vista === 'tabla' ? 'sel' : ''}`}
        aria-pressed={vista === 'tabla'}
        onClick={() => poner('tabla')}
        data-prueba="ver-tabla"
      >
        Tabla
      </button>
    </div>
  );
}
